import React from 'react';
import { motion } from 'framer-motion';
import { FiTrendingUp, FiSearch, FiBarChart2, FiUsers, FiTarget, FiZap, FiAlertCircle, FiCheckCircle, FiDownload, FiArrowRight } from 'react-icons/fi';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const DigitalMarketingGuidePage: React.FC = () => {
  useDocumentTitle(
    'The Complete Digital Marketing Guide for Business Growth | Mirani Marketing',
    'Master digital marketing with our comprehensive guide. Learn SEO, Google Ads, social media, funnel strategy, automation, and performance metrics to scale your business.'
  );

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const corePillars = [
    {
      icon: FiSearch,
      title: 'Search Engine Optimization (SEO)',
      subtitle: 'Organic growth engine',
      color: 'from-blue-500 to-blue-600',
      paragraphs: [
        'SEO is the foundation of sustainable digital marketing. Unlike paid ads that stop delivering results when you stop paying, SEO builds long-term organic visibility. It focuses on ranking your website higher on Google for keywords your target customers are actively searching for.',
        'Modern SEO is not just about keywords--it involves technical optimization, content quality, user experience, mobile responsiveness, and backlink authority. Google\'s algorithm considers over 200 ranking factors, making SEO a comprehensive discipline that requires expertise and ongoing effort.',
        'The ROI of SEO compounds over time. While paid ads offer immediate results, SEO efforts from 6 months ago continue to drive traffic today. Businesses that invest in SEO early gain a compounding advantage, capturing market share while competitors rely solely on expensive paid traffic.'
      ]
    },
    {
      icon: FiTarget,
      title: 'Google Ads',
      subtitle: 'High-intent paid acquisition',
      color: 'from-green-500 to-green-600',
      paragraphs: [
        'Google Ads targets users with high purchase intent--people actively searching for solutions you offer. When someone searches "best CRM for small business," they are much closer to buying than someone scrolling Instagram. This makes Google Ads one of the highest-converting paid channels.',
        'Effective Google Ads campaigns require more than just bidding on keywords. You need landing page optimization, conversion tracking, negative keyword management, ad copy testing, and bid strategy optimization. Without these elements, you\'re simply burning budget with poor returns.',
        'The beauty of Google Ads is scalability. Once you find a profitable campaign structure (good keywords + high-converting landing page), you can scale budget predictably. A well-optimized Google Ads account can deliver 5x+ ROAS consistently, making it the growth engine for many businesses.'
      ]
    },
    {
      icon: FiUsers,
      title: 'Social Media Marketing',
      subtitle: 'Brand awareness + engagement',
      color: 'from-purple-500 to-purple-600',
      paragraphs: [
        'Social media builds brand awareness and creates demand among audiences who may not be actively searching for your product yet. Platforms like Facebook, Instagram, LinkedIn, and YouTube allow you to reach specific demographics, interests, and behaviors with precision targeting.',
        'Unlike search ads, social media requires creative storytelling. Your audience isn\'t looking for you--you must capture their attention. This means compelling visuals, engaging copy, video content, and authentic brand voice. Social media is where you build relationships, not just transactions.',
        'The real power of social media is in retargeting and lookalike audiences. Once you build an initial customer base, you can create lookalike audiences to find similar high-value prospects. Retargeting keeps your brand top-of-mind for people who visited your site but didn\'t convert immediately.'
      ]
    },
    {
      icon: FiTrendingUp,
      title: 'Funnel Strategy',
      subtitle: 'Structured customer journey',
      color: 'from-indigo-500 to-indigo-600',
      paragraphs: [
        'A marketing funnel is the structured path from stranger to customer. Most businesses fail because they expect immediate sales without building awareness or trust first. A proper funnel recognizes that different people are at different stages--awareness, consideration, decision--and delivers the right message at each stage.',
        'The awareness stage builds visibility through content marketing, SEO, and social media. The consideration stage nurtures leads with educational content, case studies, and comparisons. The decision stage converts with offers, demos, free trials, and strong calls-to-action. Each stage requires different messaging and tactics.',
        'Advanced funnels include post-purchase strategies--onboarding, upselling, retention campaigns, and referral programs. Customer lifetime value (LTV) often matters more than first purchase. A business with a $100 first purchase but $500 LTV will outcompete one with $150 first purchase and $200 LTV.'
      ]
    },
    {
      icon: FiZap,
      title: 'Marketing Automation',
      subtitle: 'Lead nurturing & retention systems',
      color: 'from-orange-500 to-orange-600',
      paragraphs: [
        'Marketing automation delivers the right message to the right person at the right time--automatically. When someone downloads a guide, they receive a follow-up email sequence. When they abandon a cart, they get a reminder. When they haven\'t purchased in 60 days, they receive a re-engagement offer. All without manual effort.',
        'Automation scales personalization. Instead of sending the same email blast to everyone, automation platforms segment audiences based on behavior, purchase history, engagement level, and demographics. This increases relevance, engagement, and conversions dramatically.',
        'The most profitable automation workflows include welcome sequences, abandoned cart recovery, post-purchase onboarding, lead nurturing drips, re-engagement campaigns, and referral programs. Businesses with strong automation systems increase revenue by 10-30% without increasing ad spend.'
      ]
    }
  ];

  const mistakes = [
    {
      title: 'Running ads without tracking',
      description: 'Many businesses run Facebook or Google Ads without proper conversion tracking. They have no idea which campaigns are profitable. Without tracking, you\'re flying blind--unable to scale winners or cut losers. Install Google Tag Manager, Facebook Pixel, and conversion tracking before spending a rupee.'
    },
    {
      title: 'No landing page optimization',
      description: 'Driving traffic to your homepage or a poorly designed page kills conversions. Every ad campaign needs a dedicated landing page with one clear goal, compelling copy, social proof, and a strong call-to-action. A/B testing landing pages can 2-3x your conversion rate.'
    },
    {
      title: 'No retargeting',
      description: 'Only 2-3% of first-time visitors convert. The other 97% leave, and most businesses never reach them again. Retargeting shows ads to people who visited your site, keeping your brand top-of-mind. Retargeting campaigns often have 5-10x better ROI than cold traffic campaigns.'
    },
    {
      title: 'Poor conversion structure',
      description: 'Asking for a sale immediately without building trust fails. High-ticket or complex products need a funnel: offer a free guide/webinar first, nurture leads with email, then pitch the sale. Businesses that structure their funnel properly increase conversions by 40-60%.'
    }
  ];

  const metrics = [
    {
      name: 'CTR (Click-Through Rate)',
      description: 'Percentage of people who click your ad after seeing it. Indicates ad relevance and creative quality.',
      benchmark: 'Good: 2-5% for Google, 1-3% for Facebook'
    },
    {
      name: 'Conversion Rate',
      description: 'Percentage of visitors who complete desired action (purchase, signup, download). Measures landing page effectiveness.',
      benchmark: 'Good: 2-5% e-commerce, 5-10% lead gen'
    },
    {
      name: 'ROAS (Return on Ad Spend)',
      description: 'Revenue generated for every rupee spent on ads. The ultimate profitability metric for paid campaigns.',
      benchmark: 'Good: 3-5x minimum, 5-10x excellent'
    },
    {
      name: 'Customer Acquisition Cost (CAC)',
      description: 'Total marketing spend divided by number of customers acquired. Must be significantly lower than LTV.',
      benchmark: 'Good: 1/3 of Customer LTV or less'
    },
    {
      name: 'Lifetime Value (LTV)',
      description: 'Total revenue a customer generates over their relationship with your business. Critical for long-term growth.',
      benchmark: 'Good: 3x CAC or higher'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <motion.section
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-600 text-white py-24 overflow-hidden"
      >
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-6 mx-auto"
          >
            <FiBarChart2 className="text-5xl" />
          </motion.div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-center leading-tight">
            The Complete Digital Marketing Guide for Business Growth
          </h1>
          <p className="text-xl text-white/90 text-center max-w-3xl mx-auto leading-relaxed">
            Digital marketing is no longer about just posting on social media. It is about structured strategy, performance tracking, and measurable ROI.
          </p>
        </div>
      </motion.section>

      {/* Evolution of Digital Marketing */}
      <section className="py-20 max-w-6xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Evolution of Digital Marketing</h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg flex items-center justify-center mb-4">
                <FiAlertCircle className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Traditional vs Digital</h3>
              <p className="text-gray-600 leading-relaxed">
                Traditional marketing (TV, print, billboards) was expensive, untrackable, and one-way communication. You couldn't measure ROI accurately. Digital marketing is measurable, targetable, and interactive. You know exactly which campaign drove which sale, allowing data-driven optimization.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <FiBarChart2 className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Data-Driven Marketing</h3>
              <p className="text-gray-600 leading-relaxed">
                Modern marketing is built on data. Every click, view, and conversion is tracked. This enables continuous optimization--testing ad creatives, targeting different audiences, refining landing pages. Data removes guesswork and replaces it with evidence-based decisions.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-4">
                <FiTrendingUp className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Performance Marketing Shift</h3>
              <p className="text-gray-600 leading-relaxed">
                The shift from "brand awareness" to performance marketing means you only pay for results. Instead of paying for impressions, you pay for clicks, leads, or sales. This accountability forces better strategy and higher ROI. Performance marketing scales profitably.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Core Pillars */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Core Pillars of Digital Marketing</h2>
            <p className="text-xl text-gray-600 text-center mb-16 max-w-3xl mx-auto">
              Master these five pillars to build a complete, scalable digital marketing system.
            </p>

            <div className="space-y-16">
              {corePillars.map((pillar, index) => (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-xl overflow-hidden"
                >
                  <div className={`bg-gradient-to-r ${pillar.color} p-8`}>
                    <div className="flex items-center gap-4 text-white">
                      <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                        <pillar.icon className="text-3xl" />
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold">{pillar.title}</h3>
                        <p className="text-white/90 text-lg mt-1">{pillar.subtitle}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-8 space-y-4">
                    {pillar.paragraphs.map((para, pIndex) => (
                      <p key={pIndex} className="text-gray-700 leading-relaxed text-lg">
                        {para}
                      </p>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Scalable Funnel */}
      <section className="py-20 max-w-6xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">How to Build a Scalable Funnel</h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            A structured funnel transforms how you acquire and retain customers. Here's the proven framework.
          </p>

          {/* Funnel Diagram */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-12 mb-12">
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {/* Traffic */}
                <div className="bg-white rounded-xl p-6 shadow-lg mb-6 border-l-4 border-indigo-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">1. Traffic</h3>
                      <p className="text-gray-600">Drive targeted visitors through SEO, Google Ads, social media, and content marketing.</p>
                    </div>
                    <FiArrowRight className="text-3xl text-indigo-500" />
                  </div>
                </div>

                {/* Lead */}
                <div className="bg-white rounded-xl p-6 shadow-lg mb-6 border-l-4 border-purple-500 ml-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">2. Lead Capture</h3>
                      <p className="text-gray-600">Convert visitors into leads with lead magnets, forms, chatbots, and compelling offers.</p>
                    </div>
                    <FiArrowRight className="text-3xl text-purple-500" />
                  </div>
                </div>

                {/* Nurture */}
                <div className="bg-white rounded-xl p-6 shadow-lg mb-6 border-l-4 border-blue-500 ml-16">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">3. Nurture</h3>
                      <p className="text-gray-600">Build trust through email sequences, retargeting, educational content, and social proof.</p>
                    </div>
                    <FiArrowRight className="text-3xl text-blue-500" />
                  </div>
                </div>

                {/* Conversion */}
                <div className="bg-white rounded-xl p-6 shadow-lg mb-6 border-l-4 border-green-500 ml-24">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">4. Conversion</h3>
                      <p className="text-gray-600">Close sales with optimized landing pages, sales calls, demos, and limited-time offers.</p>
                    </div>
                    <FiArrowRight className="text-3xl text-green-500" />
                  </div>
                </div>

                {/* Retention */}
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 shadow-lg ml-32 text-white">
                  <h3 className="text-2xl font-bold mb-2">5. Retention & Growth</h3>
                  <p className="text-white/90">Maximize LTV through upsells, cross-sells, referral programs, and retention campaigns.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <p className="text-gray-700 leading-relaxed text-lg mb-4">
              Most businesses focus only on traffic and conversion, ignoring the middle stages. This creates leaky funnels where 90%+ of visitors leave without converting. A proper funnel captures leads, nurtures them with value, and converts them when they\'re ready--not before.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              The retention stage is often the most profitable. Acquiring a new customer costs 5-7x more than retaining an existing one. Businesses that focus on retention (email marketing, loyalty programs, upsells) achieve 30-50% higher profitability than those chasing new customers constantly.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Common Mistakes */}
      <section className="py-20 bg-red-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Common Mistakes Businesses Make</h2>
            <p className="text-xl text-gray-600 text-center mb-16 max-w-3xl mx-auto">
              Avoid these critical errors that kill marketing ROI.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {mistakes.map((mistake, index) => (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-500"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <FiAlertCircle className="text-red-600 text-xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{mistake.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{mistake.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="py-20 max-w-6xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Performance Metrics You Must Track</h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            These five metrics determine the success or failure of your marketing campaigns.
          </p>

          <div className="space-y-6">
            {metrics.map((metric, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-r from-white to-gray-50 rounded-xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{metric.name}</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">{metric.description}</p>
                    <div className="flex items-center gap-2">
                      <FiCheckCircle className="text-green-600" />
                      <span className="text-green-700 font-semibold">{metric.benchmark}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Case Study */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8">
              <h2 className="text-3xl font-bold mb-2">Case Study: E-Commerce Growth Story</h2>
              <p className="text-white/90 text-lg">How structured funnel strategy transformed ROAS</p>
            </div>
            <div className="p-8">
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-red-600 mb-2">2.1x</div>
                  <div className="text-gray-600 font-semibold">Initial ROAS</div>
                  <div className="text-sm text-gray-500 mt-1">Before Strategy</div>
                </div>
                <div className="text-center flex items-center justify-center">
                  <FiArrowRight className="text-5xl text-gray-400" />
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-600 mb-2">5.4x</div>
                  <div className="text-gray-600 font-semibold">Final ROAS</div>
                  <div className="text-sm text-gray-500 mt-1">After 90 Days</div>
                </div>
              </div>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  <strong>Challenge:</strong> A mid-sized e-commerce brand was running Google Ads and Facebook Ads but achieving only 2.1x ROAS--barely profitable after costs. They had traffic but poor conversion rates and no retargeting strategy.
                </p>
                <p className="text-lg">
                  <strong>Solution:</strong> We implemented a structured funnel: optimized landing pages for each product category, installed proper conversion tracking, launched retargeting campaigns, built email nurture sequences, and A/B tested ad creatives. We also introduced upsell funnels post-purchase.
                </p>
                <p className="text-lg">
                  <strong>Results:</strong> Within 90 days, ROAS improved to 5.4x. Conversion rate increased from 1.8% to 4.2%. Average order value grew by 35% through upsell funnels. Customer acquisition cost dropped by 40%. The business scaled profitably from ₹8L to ₹24L monthly revenue.
                </p>
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mt-6">
                  <p className="text-green-800 font-semibold text-lg">
                    <FiCheckCircle className="inline mr-2" />
                    Key Takeaway: Structured strategy beats random tactics. Funnel optimization, tracking, and automation are force multipliers.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <FiDownload className="text-4xl" />
            </div>
            <h2 className="text-4xl font-bold mb-6">Download the Full Digital Marketing Guide</h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Get the complete 50-page PDF guide with checklists, templates, and step-by-step implementation frameworks. Everything you need to build a profitable digital marketing system.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-indigo-600 px-10 py-5 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all inline-flex items-center gap-3"
            >
              <FiDownload className="text-2xl" />
              Download Free Guide (PDF)
            </motion.button>
            <p className="text-sm text-white/70 mt-6">
              No credit card required • Instant access • 10,000+ downloads
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DigitalMarketingGuidePage;
